import { env } from './env';
import type {
  AdHocUploadUrlResponse,
  AdminGarmentSummary,
  CatalogPage,
  CategoriesResponse,
  CreateGarmentBody,
  DevicePassword,
  GarmentIndexResponse,
  GarmentSummary,
  GenerateResponse,
  GenerationsResponse,
  ImageSlot,
  Member,
  Quota,
  ReorderEntry,
  StoreCategory,
  SupportedPhotoType,
  UpdateGarmentBody,
  UploadUrlResponse,
} from './types';

/* ---------- Token plumbing ------------------------------------------------ */

/**
 * Clerk's `getToken` lives behind a React hook, but the API client is a plain
 * module. `ApiTokenBridge` (rendered inside ClerkProvider) installs the getter
 * here once on mount.
 */
type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter = async () => null;
let onUnauthorized: () => void = () => {};

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

/**
 * Called on any 401. Both causes — the session being revoked because the same
 * credential signed in elsewhere, and a deactivated device whose `active` claim
 * is now false — are handled the same way: sign out and return to login.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/* ---------- Errors -------------------------------------------------------- */

/** Every error body the API can produce. See `detailFromBody`. */
export interface ApiErrorBody {
  error?: string;
  detail?: string;
  fields?: { path?: string; message?: string }[];
}

/**
 * The server's explanation, pulled out of whichever shape it arrived in.
 *
 * ⚠️ **The API has two, and only one of them is `detail`.** `ApiError.toJSON`,
 * constraint failures and the parked master-admin stubs all send `{ error,
 * detail }` — but a **ZodError sends `fields` and no `detail` at all**, and that
 * is the *most common* admin 400: an empty name, a price sent as a string,
 * `category: null`, or `updateGarmentSchema`'s "nothing to patch". Reading only
 * `detail` leaves exactly those rendering the screen's generic sentence with the
 * diagnosis sitting unread in the payload — which is the bug this replaced,
 * one field name off.
 *
 * ℹ️ Returns `undefined` when the server explained nothing — a production 500,
 * `not_found`, a non-JSON body. That is load-bearing: it is what lets `detailOf`
 * fall through to the screen's own copy rather than showing a bare code.
 *
 * 🔒 Everything here is safe to display. `error-handler.ts` puts field *names*
 * and Zod messages only into the `fields` branch — never the submitted values —
 * because on three routes that value is a Gemini key or a device password.
 */
export function detailFromBody(payload: ApiErrorBody): string | undefined {
  if (payload.detail) return payload.detail;
  if (!payload.fields?.length) return undefined;

  // A root-level issue has an empty `path` — the `.refine` above is one — so the
  // name is omitted rather than rendered as a leading colon.
  const parts = payload.fields
    .map((field) => [field.path, field.message].filter(Boolean).join(': '))
    .filter(Boolean);

  return parts.length > 0 ? parts.join('; ') : undefined;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
    /**
     * The server's own explanation, kept **separate from `message`** rather than
     * folded into it. `message` always holds something renderable, so a screen
     * that shows it cannot tell "the API explained what went wrong" apart from
     * "there was a 400"; this is `undefined` in the second case, which is what
     * lets `detailOf` fall back to the screen's own copy.
     */
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The server's explanation, or `undefined` when it did not give one — a 500, a
 * dropped connection, a non-JSON body.
 *
 * ⚠️ **Use it as `detailOf(err) ?? 'the screen's own sentence'`, never on its
 * own.** The fallbacks are not filler: several were written for the failure that
 * produces *no* detail, and `DeviceList`'s half-applied password warning is the
 * clearest case — it is the right thing to say precisely when the server said
 * nothing.
 */
export function detailOf(err: unknown): string | undefined {
  return err instanceof ApiError ? err.detail : undefined;
}

/**
 * The request never got an answer — the device went offline, the app was
 * backgrounded, or iOS purged the page. Distinct from `ApiError` because for a
 * generation it means the work may well have succeeded and been billed.
 */
export class NetworkError extends Error {
  constructor(message = 'Could not reach the server') {
    super(message);
    this.name = 'NetworkError';
  }
}

/* ---------- Core request -------------------------------------------------- */

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Multipart bodies bypass JSON encoding. */
  formData?: FormData;
  query?: Record<string, string | number | string[] | undefined>;
  /**
   * Milliseconds. Defaults to 20s, which suits every route except
   * `/try-on/generate` — that one holds the connection for the whole generation.
   */
  timeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * Computed once — `env.apiBaseUrl` is fixed for the life of the bundle. Matches
 * both `*.ngrok.io` and `*.ngrok-free.app`. Cloudflare tunnels need no
 * equivalent; they proxy straight through with no interstitial.
 */
const isTunnelledApi = /\bngrok\b/.test(env.apiBaseUrl);

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(env.apiBaseUrl + path);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        // The API reads repeated keys for multi-category filters.
        for (const v of value) url.searchParams.append(key, v);
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, query, timeoutMs = 20_000, signal } = options;

  /**
   * ⚠️ **Guarded, and it was not.** `getToken` sat above the try/catch below, so
   * a Clerk refresh failure escaped this module as a raw error — past every
   * caller that narrows on `ApiError`/`NetworkError`, and out to the customer as
   * an unexplained failure with nothing naming the cause.
   *
   * It is a real path, not a theoretical one: session tokens last ~60s and are
   * refreshed on demand, so the moment it fails is the moment the app comes back
   * from being backgrounded — which is exactly what picking a photo out of the
   * system gallery does.
   */
  let token: string | null;
  try {
    token = await getToken();
  } catch (err) {
    throw new ApiError(
      401,
      'token_unavailable',
      `Could not refresh the session: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  /**
   * Development only, and self-disabling: a free ngrok tunnel answers anything
   * with a browser-shaped `User-Agent` with its own HTML interstitial instead of
   * proxying. Every call would then return a 200 full of HTML, which surfaces
   * here as a JSON parse failure rather than as anything mentioning ngrok. The
   * header opts out of that page; it is sent only when the base URL is actually
   * a tunnel, so it disappears by itself in production.
   */
  if (isTunnelledApi) headers['ngrok-skip-browser-warning'] = 'true';

  // Combine the caller's signal with our own deadline so either can abort.
  const timeout = AbortSignal.timeout(timeoutMs);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal: combined,
    });
  } catch (err) {
    // AbortError, TypeError: Failed to fetch, undici's mid-body "terminated" —
    // all mean the same thing to a caller: no answer arrived.
    if (signal?.aborted) throw err;
    throw new NetworkError();
  }

  if (response.status === 401) {
    onUnauthorized();
    throw new ApiError(401, 'unauthorized', 'Signed out');
  }

  if (!response.ok) {
    let code: string | undefined;
    let detail: string | undefined;
    let message = `Request failed (${response.status})`;
    try {
      /**
       * ⚠️ **The field is `detail`, not `message`.** `ApiError.toJSON` in the API
       * serialises `{ error, detail, ...meta }` and has no `message` key at all,
       * so reading one meant this fell back to the generic string on *every*
       * 4xx — the server's diagnosis was fetched, parsed, and thrown away.
       *
       * The admin screens are what that cost. `incomplete_list` on reorder
       * rendered as "Try again", where trying again fails forever, while the
       * detail said the payload covered 12 of 14 garments and to reload the
       * screen. The customer flow was never affected: `customer/errors.ts` maps
       * `code` to its own wording and never reads this.
       *
       * 🔒 Safe to show. `error-handler.ts` puts field *names* only into a
       * validation 400 — never the values — because on three routes the value is
       * a Gemini key or a device password.
       */
      const payload = (await response.json()) as ApiErrorBody;
      code = payload.error;
      detail = detailFromBody(payload);
      if (detail) message = detail;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new ApiError(response.status, code, message, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* ---------- Customer flow ------------------------------------------------- */

/**
 * `storeId` is never sent — the API reads it from the JWT. `offset` is the
 * caller's to manage, and a change of `categories` must reset it to 0.
 */
export function getCatalog(params: {
  limit: number;
  offset: number;
  categories?: string[];
}): Promise<CatalogPage<GarmentSummary>> {
  return request('/catalog', {
    query: {
      limit: params.limit,
      offset: params.offset,
      categories: params.categories?.length ? params.categories : undefined,
    },
  });
}

/** The *in-stock* category list — drives the filter chips. */
export function getCategories(): Promise<CategoriesResponse> {
  return request('/catalog/categories');
}

/**
 * Every garment's name, unpaged — the index `CatalogBrowser` filters as the
 * customer types. Fetched once per session rather than per keystroke, so search
 * does not depend on shop wifi once the screen has loaded.
 */
export function getGarmentIndex(): Promise<GarmentIndexResponse> {
  return request('/catalog/garment-index');
}

/**
 * `contentType` is signed into the returned presigned URL, so it has to be the
 * file's real type. Declaring the wrong one fails at R2 with an opaque 403
 * `SignatureDoesNotMatch`, not here.
 */
export function getUploadUrl(contentType: SupportedPhotoType): Promise<UploadUrlResponse> {
  return request('/try-on/upload-url', { method: 'POST', body: { contentType } });
}

/**
 * Uploads straight to R2 — the photo never passes through the API server, which
 * is exactly why every failure here has to carry its own diagnosis: nothing
 * server-side ever sees this request, so the API logs stop at
 * `upload_url_issued` and say nothing about what happened next.
 *
 * ⚠️ **`contentType` is the type that was signed, and it is a parameter rather
 * than `file.type` for a reason.** SigV4 folds `Content-Type` into the
 * signature, so R2 recomputes against whatever the browser sends and answers a
 * mismatch with an opaque `403 SignatureDoesNotMatch`. `file.type` is *not*
 * reliably that value: the caller passes it through `normaliseType` first, which
 * rewrites the `image/jpg` phones send into the real `image/jpeg`, lowercases a
 * shouted type, and strips a `;` parameter. Sending `file.type` here meant the
 * url was signed for one string and the PUT presented another, on exactly the
 * devices whose type needed rewriting.
 */
export async function uploadPhoto(
  uploadUrl: string,
  file: File,
  contentType: SupportedPhotoType,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw err;

    /**
     * ⚠️ A blocked preflight and a dead network are **the same `TypeError`** to
     * JavaScript — the CORS decision is made by the browser and deliberately
     * not reported to script. So this cannot name the cause, and it must not
     * pretend to; `upload_unreachable` is the code, and the two candidates are
     * spelled out for whoever is reading rather than collapsed into "offline".
     *
     * If it is CORS, the fix is a policy on the R2 bucket allowing `PUT` and the
     * `content-type` header from this origin. Nothing in the app can fix it.
     */
    throw new NetworkError('upload_unreachable');
  }

  if (!response.ok) {
    /**
     * R2 answers with an S3-style XML error document. `<Code>` is the only part
     * worth keeping — `SignatureDoesNotMatch` versus an expired url are the same
     * 403 and read identically without it.
     */
    let code = `http_${response.status}`;
    try {
      const body = await response.text();
      const match = /<Code>([^<]+)<\/Code>/.exec(body);
      if (match?.[1]) code = match[1];
    } catch {
      /* no readable body — the status-derived code is still better than nothing */
    }

    throw new ApiError(response.status, code, `Storage rejected the upload (${code})`);
  }
}

/**
 * `POST /try-on/adhoc-upload-url` — the presigned PUT for an **ad-hoc garment**,
 * photographed at the counter for one try-on with no catalogue row behind it.
 *
 * The bytes then go up through `uploadPhoto` like any other: the function is
 * named for its first caller but is just a signed PUT, and the garment's
 * failures are the customer photo's failures.
 *
 * ⚠️ **Into the PUBLIC bucket** — see `AdHocUploadUrlResponse`. The one
 * consequence for this client is that a missing `PUT` CORS rule on that bucket
 * looks exactly like being offline, because the browser does not tell script
 * which it was.
 */
export function getAdHocUploadUrl(
  contentType: SupportedPhotoType,
): Promise<AdHocUploadUrlResponse> {
  return request('/try-on/adhoc-upload-url', { method: 'POST', body: { contentType } });
}

/**
 * The generation call — the server holds the connection for it (~10s).
 *
 * Note this resolves with `{ success: false, reason }` for business failures —
 * those are 200s, not HTTP errors. Only transport failures throw.
 *
 * ⚠️ **The 150s ceiling exists to sit ABOVE the API's `GEMINI_TIMEOUT_MS`
 * (120s), and that ordering is the whole point of the number.** Below it, this
 * client gives up on a generation the server is still running: the customer sees
 * a failure, Gemini bills for the image, and **no refund happens — because
 * server-side nothing failed.** The API's own deadline is what produces
 * `generation_timeout` and the refund that goes with it, so this must never fire
 * first.
 *
 * It was 90s against an API deadline of 60s. The API's moved to 120s and this
 * did not, which left every generation between 90s and 120s in exactly the
 * silently-charged state above. **Raising `GEMINI_TIMEOUT_MS` means raising this
 * in the same change** — the two live in different repos and nothing enforces
 * the ordering but this comment.
 */
export function generateTryOn(
  body: { photoKey: string; garmentId: string; visitId?: string },
  signal?: AbortSignal,
): Promise<GenerateResponse> {
  return request('/try-on/generate', {
    method: 'POST',
    body,
    timeoutMs: 150_000,
    signal,
  });
}

/**
 * The same generation call for an **ad-hoc garment** — one photographed at the
 * counter instead of picked from the catalogue.
 *
 * ⚠️ **The 150s ceiling is repeated rather than shared, and it must stay equal
 * to the one above.** Both sit above the API's `GEMINI_TIMEOUT_MS` for the same
 * reason, and a lower value here would put ad-hoc generations into the
 * silently-charged state that comment describes while catalogue ones stayed
 * correct — the worst shape of that bug, because it would look like ad-hoc
 * "failing more".
 *
 * `category` names a `store_categories` row and resolves server-side to the
 * prompt. `name` is the shop's own label for the rail card; it reaches neither
 * Gemini nor a database.
 */
export function generateAdHocTryOn(
  body: { photoKey: string; garmentKey: string; category: string; name?: string; visitId?: string },
  signal?: AbortSignal,
): Promise<GenerateResponse> {
  return request('/try-on/generate-adhoc', {
    method: 'POST',
    body,
    timeoutMs: 150_000,
    signal,
  });
}

/* ---------- Admin: catalog ------------------------------------------------ */

export function getAdminGarments(params: {
  storeId: string;
  limit: number;
  offset: number;
  categories?: string[];
}): Promise<CatalogPage<AdminGarmentSummary>> {
  return request('/catalog/garments', {
    query: {
      storeId: params.storeId,
      limit: params.limit,
      offset: params.offset,
      categories: params.categories?.length ? params.categories : undefined,
    },
  });
}

/**
 * The two shapes `GET /catalog/garments/:id` may answer with — see `getGarment`.
 * Everything is optional because exactly one of the two naming styles is present
 * on any given response, and which one is a property of the server, not the call.
 */
type GarmentResponse = Partial<Omit<AdminGarmentSummary, 'price'>> & {
  id?: string;
  price?: number | string | null;
  display_image_url?: string | null;
  source_image_url?: string | null;
  thumb_image_url?: string | null;
  display_order?: number;
};

/**
 * ⚠️ **This route answers with the raw database row — every other garment read
 * does not.** `GET /catalog/garments` and `/catalog/garments/all` map through the
 * server's `toAdminSummary` first; `GET /catalog/garments/:id` returns the row as
 * it came out of Postgres, so the fields arrive as `source_image_url`,
 * `display_image_url`, `display_order`, and `price` as the *string* node-postgres
 * hands back for a `numeric`.
 *
 * Read as an `AdminGarmentSummary` that is `undefined` in every field the edit
 * screen needs, which is not a blank screen but a **misleading** one: an uploaded
 * image reads as "Not uploaded" over a file that is really in R2, the price box
 * fills with the literal text `undefined`, the "cannot be tried on" warning can
 * never fire because `undefined === null` is false, and Save throws on
 * `name.trim()`.
 *
 * ⚠️ **Both shapes are read, camelCase first, and that is the point of doing this
 * here.** Normalising in the client rather than the server leaves the API contract
 * untouched — but it also means the day that route is made consistent with its
 * siblings, this mapping would start returning `undefined` for everything and
 * bring the same bug back. Falling back rather than switching makes that a
 * no-op.
 */
export async function getGarment(id: string): Promise<AdminGarmentSummary> {
  const row = await request<GarmentResponse>(`/catalog/garments/${encodeURIComponent(id)}`);

  return {
    garmentId: row.garmentId ?? row.id ?? id,
    name: row.name ?? '',
    category: row.category ?? '',
    price: toPrice(row.price),
    displayImageUrl: row.displayImageUrl ?? row.display_image_url ?? null,
    sourceImageUrl: row.sourceImageUrl ?? row.source_image_url ?? null,
    thumbImageUrl: row.thumbImageUrl ?? row.thumb_image_url ?? null,
    displayOrder: row.displayOrder ?? row.display_order ?? 0,
  };
}

/**
 * ⚠️ `price` is `numeric` in Postgres, and node-postgres hands numerics back as
 * **strings** — `"1200.00"`, not `1200` — because a JS number cannot hold every
 * numeric losslessly. `toAdminSummary` does this same `Number()` server-side, so
 * without it here the price field would read `1200.00` where every other screen
 * shows `1200`.
 */
function toPrice(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  const price = Number(value);
  return Number.isFinite(price) ? price : null;
}

/** `display_order` is computed server-side as MAX + 10 — never send it here. */
export function createGarment(body: CreateGarmentBody): Promise<{ garmentId: string }> {
  return request('/catalog/garments', { method: 'POST', body });
}

export function updateGarment(id: string, body: UpdateGarmentBody): Promise<unknown> {
  return request(`/catalog/garments/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}

export function deleteGarment(id: string): Promise<unknown> {
  return request(`/catalog/garments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * One file per request, two per garment. Goes through the API server rather
 * than presigned — deliberately the opposite of the customer-photo path, so the
 * row is only written once the object has really landed.
 *
 * The server sniffs the real format from the file's magic bytes and ignores the
 * declared mimetype entirely, so there is nothing to declare here.
 */
export function uploadGarmentImage(
  id: string,
  slot: ImageSlot,
  file: File,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file);
  return request(`/catalog/garments/${encodeURIComponent(id)}/images/${slot}`, {
    method: 'POST',
    formData,
    // Garment photography is large and shop wifi is slow; this is an off-hours
    // single-operator path, so a generous ceiling costs nothing.
    timeoutMs: 120_000,
  });
}

/**
 * Save sends the **complete** list, in final order; the server renumbers to clean
 * stride-10 and returns how many rows moved.
 *
 * ⚠️ **Wrapped in `{ garments }`, not sent as a bare array.** A top-level array has
 * nowhere to put `storeId`, which master_admin needs — its token carries no claims
 * — so the API takes the same `{ storeId?, … }` shape as bulk-create and
 * bulk-delete. We omit `storeId`: a Clerk caller's store comes from the JWT, and
 * sending it would only be a value the server has to check against the token.
 *
 * ⚠️ **The `display_order` values are a ranking, not positions.** The server sorts
 * by them and rewrites every row as 10, 20, 30…, so numbering from list index is
 * what guarantees the saved order matches the screen — ties would otherwise be
 * broken by `created_at`, which the operator cannot see.
 *
 * A `400` here is always all-or-nothing and always means the client is stale:
 * `unknown_ids` (a garment was deleted), `incomplete_list` (one was added), or
 * `duplicate_ids` (a client bug). Nothing was written, so the fix is to reload.
 */
export function reorderGarments(garments: ReorderEntry[]): Promise<{ updated: number }> {
  return request('/catalog/garments/reorder', { method: 'PATCH', body: { garments } });
}

export function getStoreCategories(storeId: string): Promise<StoreCategory[]> {
  return request('/catalog/store-categories', { query: { storeId } });
}

/**
 * Reorder mode needs every garment at once — drag-and-drop cannot move a tile
 * from page 3 to page 1, so a paged reorder view is a contradiction.
 *
 * ℹ️ **This used to page through `GET /catalog/garments` at the cap and stitch the
 * results**, because the deliberate unpaged route did not exist. It does now, so
 * the stopgap and its `hasMore` guard are gone. Worth knowing why the fallback was
 * never simply `limit=1000`: the cap exists precisely to stop one call pulling the
 * whole catalogue, and using it as the bypass would defeat it while looking like
 * compliance. That reasoning is what made this a route rather than a parameter.
 *
 * Returns a bare array — there is no `hasMore` here, and the different shape is
 * the signal that this is a different contract from the paged read.
 */
export function getAllGarments(storeId: string): Promise<AdminGarmentSummary[]> {
  return request('/catalog/garments/all', { query: { storeId } });
}

/* ---------- Admin: quota and devices -------------------------------------- */

export function getQuota(storeId: string): Promise<Quota> {
  return request(`/quota/${encodeURIComponent(storeId)}`);
}

export function getMembers(storeId: string): Promise<Member[]> {
  return request(`/stores/${encodeURIComponent(storeId)}/members`);
}

/** 🔒 Returns a plaintext credential. Never log or persist the result. */
export function getDevicePassword(memberId: string): Promise<DevicePassword> {
  return request(`/members/${encodeURIComponent(memberId)}/password`);
}

export function setDevicePassword(
  memberId: string,
  password: string,
): Promise<{ memberId: string; username: string; updated: boolean }> {
  return request(`/members/${encodeURIComponent(memberId)}/password`, {
    method: 'PATCH',
    body: { password },
  });
}

export function forceLogout(
  memberId: string,
): Promise<{ memberId: string; sessionsRevoked: number }> {
  return request(`/members/${encodeURIComponent(memberId)}/force-logout`, { method: 'POST' });
}

/**
 * `GET /try-on/generations?visitId=…` — **the recovery read.**
 *
 * Asked once when the try-on screen mounts, to find out what the server did
 * while this device was not listening. A generation is not cancelled by its
 * client disconnecting, so a result lost to a wifi blip is still sitting on the
 * server, already paid for.
 *
 * ⚠️ **A short timeout, unlike the generate calls.** This is a small database
 * read on the critical path of *rendering the screen*, not a generation — the
 * default is right and 150s would leave the rail blank for two and a half
 * minutes on a bad network rather than failing fast and letting the local
 * IndexedDB copy stand alone.
 */
export function listGenerations(
  visitId: string,
  signal?: AbortSignal,
): Promise<GenerationsResponse> {
  return request('/try-on/generations', { query: { visitId }, signal });
}
